import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const ModalRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${theme.colors.surfaceContainerBackground};

  > form {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
`

export const ModalHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  padding: ${theme.spacing.xLarge}px ${theme.spacing.xLarge}px ${theme.spacing.small}px;

  .title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xxxxLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 32px;
    text-align: left;
  }

  .close-button {
    position: absolute;
    top: ${theme.spacing.small}px;
    right: ${theme.spacing.small}px;

    html[data-theme='light'] & svg path {
      fill: ${theme.colors.menuItemText};
    }
  }
`

export const ModalBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  /* Top room for the first field's floating label, which sits above its border. */
  padding: ${theme.spacing.xSmall}px ${theme.spacing.xLarge}px ${theme.spacing.medium}px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  .explanation {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;
    text-align: left;
  }

  .teaser {
    color: ${theme.colors.cta};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 22px;
    text-align: center;
  }

  .MuiInputLabel-root {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};

    &.Mui-focused {
      color: ${theme.colors.cta};
    }

    &.Mui-error {
      color: ${theme.colors.errorLight};
    }

    .MuiFormLabel-asterisk {
      color: #ee4359;
    }
  }

  .MuiInputBase-root {
    background: transparent;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;

    .MuiInputBase-input::placeholder {
      color: ${theme.colors.devicesSecondaryText};
      opacity: 1;
    }

    .MuiInputBase-input.Mui-disabled {
      -webkit-text-fill-color: ${theme.colors.devicesSecondaryText};
    }

    fieldset {
      border: 1px solid ${theme.colors.borderLow};

      html[data-theme='light'] & {
        border-color: ${theme.colors.dropdownBorder};
      }
    }

    &:hover fieldset {
      border-color: ${theme.colors.controlBorder};
    }

    &.Mui-focused fieldset {
      border-color: ${theme.colors.cta};
    }
  }

  .MuiFormHelperText-root.Mui-error {
    color: ${theme.colors.errorLight};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    margin-left: 0;
  }
`

export const ConditionsPanel = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.medium}px;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background: ${theme.colors.labelCardBg};

  html[data-theme='light'] & {
    border: 1px solid ${theme.colors.dropdownBorder};
  }

  .conditions-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing.small}px;
    min-height: 50px;
  }

  .conditions-title-group,
  .conditions-header-actions {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
  }

  .conditions-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;
  }

  .conditions-hint {
    display: flex;
    cursor: help;

    svg {
      width: 16px;
      height: 16px;
    }

    svg path {
      fill: ${theme.colors.devicesSecondaryText};
    }

    &:hover svg path {
      fill: ${theme.colors.cta};
    }
  }

  .conditions-toggle {
    padding: ${theme.spacing.half}px;
    color: ${theme.colors.menuItemText};

    svg {
      transition: transform 200ms ease;
    }

    &.open svg {
      transform: rotate(180deg);
    }
  }

  .conditions-body {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.medium}px;
    padding-top: ${theme.spacing.medium}px;
  }

  .match-control {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;
  }

  .match-select {
    min-width: 92px;
  }

  .condition.flagged .MuiInputBase-root fieldset {
    border-color: ${theme.colors.errorLight};
  }

  .condition-error,
  .server-error {
    margin: ${theme.spacing.xSmall}px 0 0 36px;
    color: ${theme.colors.errorLight};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    line-height: 18px;
  }

  .server-error {
    margin-left: 0;
  }

  .server-error-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing.small}px;

    .server-error {
      margin-top: 0;
    }
  }
`

export const ConditionRow = styled(Box)`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) 32px;
  align-items: center;
  gap: ${theme.spacing.xSmall}px;

  .prefix {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;
    text-align: center;
  }

  /* One height for every field in the row, so the value cell lines up with the two selects. */
  .MuiInputBase-root {
    height: 55px;
  }

  /* The first value truncates so the +N count always stays visible. */
  .multi-value {
    display: flex;
    gap: ${theme.spacing.xSmall}px;
    min-width: 0;
  }

  .multi-value__first {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .multi-value__more {
    flex-shrink: 0;
    color: ${theme.colors.cta};
  }

  .value-cell .date-input {
    width: 100%;
    min-width: 0;
  }

  .delete-btn {
    color: ${theme.colors.devicesSecondaryText};
    padding: ${theme.spacing.xSmall}px;

    &:hover svg path {
      stroke: ${theme.colors.errorLight};
    }
  }

  .between-cell {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
  }
`

export const AddConditionButton = styled('button')`
  align-self: flex-end;
  border: none;
  background: transparent;
  color: ${theme.colors.cta};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: pointer;
  padding: ${theme.spacing.xSmall}px 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const ModalActions = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.small}px;
  margin-top: auto;
  padding: ${theme.spacing.medium}px ${theme.spacing.xLarge}px ${theme.spacing.large}px;

  > button {
    flex: 1;
    height: 48px;
  }
`

export const ExclusionRuleToast = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'visible' && prop !== 'durationMs'
})<{ visible?: boolean; durationMs?: number }>`
  display: flex;
  flex-direction: column;
  width: 440px;
  padding: ${theme.spacing.small}px ${theme.spacing.small}px 0 ${theme.spacing.small}px;
  gap: ${theme.spacing.small}px;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background: ${theme.colors.darkPlumTaupe};
  box-shadow: 0 4px 8px 0 rgba(30, 26, 29, 0.2);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: ${({ visible }) => (visible ? 'translateY(0)' : 'translateY(8px)')};
  transition: opacity 200ms ease, transform 200ms ease;

  html[data-theme='light'] & {
    background: #72466a;
    box-shadow: 0 4px 8px 0 rgba(162, 145, 158, 0.2);

    .toast-text {
      color: #ffffff;
    }

    .toast-progress {
      background: #ffffff;
    }

    .toast-icon svg path,
    .toast-close svg path {
      stroke: #ffffff;
    }
  }

  .toast-row {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.small}px;
    width: 100%;
  }

  .toast-icon {
    display: flex;
    flex-shrink: 0;
  }

  .toast-text {
    flex: 1;
    margin: 0;
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.extraThick};
    line-height: 20px;
  }

  .toast-close {
    flex-shrink: 0;
    padding: 0;
    margin: 0;
    color: inherit;

    &:hover {
      background: transparent;
    }
  }

  .toast-progress {
    height: 2px;
    background: ${theme.colors.white};
    transform-origin: left;
    animation: ${({ durationMs = 5000 }) =>
      Number.isFinite(durationMs) ? `exclusionRuleToastProgress ${durationMs}ms linear forwards` : 'none'};
  }

  @keyframes exclusionRuleToastProgress {
    from {
      transform: scaleX(1);
    }
    to {
      transform: scaleX(0);
    }
  }
`
